import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ArrowLeft, Images, Mail } from 'lucide-react';
import { getProjectBySlug, getProjects } from '../api';
import useSeo from '../hooks/useSeo';
import PhotoGallery from '../components/PhotoGallery';
import { toEmbedUrl } from '../lib/videoEmbed';
import { optimizeImageUrl } from '../lib/optimizeImage';

const ProjectDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showGallery, setShowGallery] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setProject(null);
    (async () => {
      try {
        const p = await getProjectBySlug(slug);
        if (cancelled) return;
        setProject(p);
        // Pull 3 same-category projects (excluding this one) as "Related work"
        const all = await getProjects(p.category);
        if (cancelled) return;
        setRelated(all.filter((r) => r.id !== p.id).slice(0, 3));
      } catch (err) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  useSeo({
    title: project
      ? `${project.title} — ${project.category} | Christian D Animator (Mansfield, TX)`
      : 'Project — Christian D Animator',
    description: project
      ? `${project.description.slice(0, 150)} — ${project.category} project by Christian D Animator, Mansfield, TX. Serving the DFW Metroplex.`
      : 'Project case study by Christian D Animator.',
    image: project ? project.imageUrl : undefined,
  });

  const isPhotoGallery = project && project.category === 'Photography' && Array.isArray(project.gallery) && project.gallery.length > 0;
  const galleryImages = isPhotoGallery
    ? [project.imageUrl, ...project.gallery.filter((u) => u !== project.imageUrl)]
    : [];

  useEffect(() => {
    if (!project) return;
    // CreativeWork JSON-LD for richer SERP appearance
    const id = 'project-jsonld';
    let el = document.head.querySelector(`#${id}`);
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    const url = `${window.location.origin}/portfolio/${project.slug}`;
    el.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CreativeWork',
      name: project.title,
      description: project.description,
      url,
      image: project.imageUrl,
      genre: project.category,
      keywords: (project.tags || []).join(', '),
      creator: { '@id': 'https://christiandanimator.studio/#person' },
      locationCreated: { '@type': 'Place', address: { '@type': 'PostalAddress', addressLocality: 'Mansfield', addressRegion: 'TX', addressCountry: 'US' } },
    });
    return () => { if (el) el.remove(); };
  }, [project]);

  if (loading) {
    return (
      <div className="min-h-screen pt-32 px-4 text-center text-gray-400" data-testid="project-loading">
        Loading…
      </div>
    );
  }
  if (notFound || !project) {
    return (
      <div className="min-h-screen pt-32 px-4 text-center" data-testid="project-not-found">
        <h1 className="text-3xl font-bold text-white mb-3">Project not found</h1>
        <p className="text-gray-400 mb-6">It may have been removed or the link is mistyped.</p>
        <Link to="/portfolio" className="text-blue-400 hover:text-blue-300 inline-flex items-center">
          <ArrowLeft size={16} className="mr-1" /> Back to portfolio
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="project-detail-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/portfolio')}
          data-testid="back-to-portfolio"
          className="inline-flex items-center text-sm text-gray-400 hover:text-blue-400 mb-6"
        >
          <ArrowLeft size={16} className="mr-1.5" /> Back to portfolio
        </button>

        <Badge className="mb-4 bg-blue-600/20 text-blue-400 border border-blue-400/30">
          {project.category}
        </Badge>
        <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="project-title">
          {project.title}
        </h1>
        <p className="text-lg text-gray-300 max-w-3xl mb-8" data-testid="project-description">
          {project.description}
        </p>

        {/* Hero media */}
        {project.videoUrl ? (
          <div className="aspect-video mb-8 rounded-xl overflow-hidden bg-black border border-gray-800">
            <iframe
              src={toEmbedUrl(project.videoUrl)}
              className="w-full h-full"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
              allowFullScreen
              title={project.title}
              data-testid="project-video"
            />
          </div>
        ) : (
          <img
            src={optimizeImageUrl(project.imageUrl, 1600)}
            alt={project.title}
            decoding="async"
            className="w-full rounded-xl mb-8 border border-gray-800"
            data-testid="project-hero-image"
          />
        )}

        {/* Photo gallery CTA for Photography projects */}
        {isPhotoGallery && (
          <Button
            onClick={() => setShowGallery(true)}
            data-testid="open-gallery-btn"
            className="mb-8 bg-blue-600 hover:bg-blue-700 text-white rounded-full"
          >
            <Images size={16} className="mr-2" /> View Gallery ({galleryImages.length} photos)
          </Button>
        )}

        {/* Tags */}
        {project.tags && project.tags.length > 0 && (
          <div className="mb-10">
            <h3 className="text-sm uppercase tracking-wide text-gray-500 mb-3">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {project.tags.map((tag) => (
                <span key={tag} className="px-3 py-1.5 bg-gray-800 text-gray-300 rounded-full text-sm">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Hire CTA */}
        <div className="rounded-xl border border-gray-800 bg-gray-900/50 p-6 sm:p-8 mb-12 text-center">
          <h2 className="text-2xl font-bold text-white mb-2">Have a similar project in mind?</h2>
          <p className="text-gray-400 mb-5 max-w-xl mx-auto">
            Based in Mansfield, TX. Serving the Dallas–Fort Worth Metroplex and remote clients worldwide.
          </p>
          <Link to="/contact">
            <Button data-testid="project-detail-cta" className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-5 text-base">
              <Mail size={16} className="mr-2" /> Start a project
            </Button>
          </Link>
        </div>

        {/* Related work */}
        {related.length > 0 && (
          <div data-testid="related-work">
            <h3 className="text-2xl font-bold mb-5">Related {project.category} work</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {related.map((r) => (
                <Link
                  key={r.id}
                  to={`/portfolio/${r.slug || r.id}`}
                  data-testid={`related-card-${r.id}`}
                  className="group rounded-lg overflow-hidden border border-gray-800 hover:border-blue-400 transition-colors"
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={optimizeImageUrl(r.imageUrl, 600)}
                      alt={r.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-medium text-white group-hover:text-blue-400 transition-colors">{r.title}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {showGallery && isPhotoGallery && (
        <PhotoGallery
          project={{ ...project, gallery: galleryImages }}
          onClose={() => setShowGallery(false)}
        />
      )}
    </div>
  );
};

export default ProjectDetail;
